import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTutorial } from '../contexts/TutorialContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import { AchievementUnlock } from '../types/battle';
import CraftingInventoryTab from './CraftingInventoryTab';
import CraftingRecipesTab from './CraftingRecipesTab';
import CraftingCraftedTab from './CraftingCraftedTab';
import './Crafting.css';

export interface Material {
  id: number;
  materialId: number;
  materialName: string;
  materialType: string;
  materialRarity: string;
  description: string;
  baseValue: number;
  sellValue: number;
  iconName: string;
  quantity: number;
  acquiredAt: string;
}

export interface Recipe {
  id: number;
  name: string;
  description: string;
  resultItemType: string | number;
  resultEquipmentSlot?: number | null;
  resultItemName: string;
  requiredLevel: number;
  requiredClass: string | null;
  craftingTime: number;
  experienceReward: number;
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  speedBonus: number;
  minDamage?: number;
  maxDamage?: number;
  minRange?: number;
  maxRange?: number;
  damageType?: string;
  aoeShape?: string;
  requiresLoS?: boolean;
  weaponAPCost?: number;
  accuracyModifier?: number;
  usesAmmo?: boolean;
  maxAmmo?: number;
  imageUrl?: string;
  iconName?: string;
  discoveredAt: string;
  goldFee: number;
  ingredients: Array<{
    materialId: number;
    materialName: string;
    materialType: string;
    materialRarity: string;
    quantity: number;
  }>;
}

export interface CraftedItem {
  id: number;
  recipeId: number;
  itemName: string;
  itemType: string;
  attackBonus: number;
  defenseBonus: number;
  hpBonus: number;
  speedBonus: number;
  specialEffects: string;
  durability: number;
  maxDurability: number;
  qualityTier: number;
  requiredLevel: number;
  isEquipped: boolean;
  equippedToCharacterId: number | null;
  equippedToCharacterName: string | null;
  recipeName: string;
  craftedAt: string;
  imageUrl?: string;
  iconName?: string;
  sellValue: number;
}

export interface CharacterOption {
  id: number;
  name: string;
  gold: number;
}

export interface MaterialSource {
  monsterId: number;
  monsterName: string;
  monsterLevel: number;
  monsterDifficulty: string;
  dropChance: number;
  minQuantity: number;
  maxQuantity: number;
  minPlayerLevel: number;
  maxPlayerLevel: number;
}

type ActiveTab = 'inventory' | 'recipes' | 'crafted-items';

const Crafting: React.FC = () => {
  const { handleAuthError } = useAuth();
  const { addNotification } = useNotifications();
  const { checkAndStartTutorial } = useTutorial();
  const [activeTab, setActiveTab] = useState<ActiveTab>('inventory');
  const [inventory, setInventory] = useState<Material[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [craftedItems, setCraftedItems] = useState<CraftedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [crafting, setCrafting] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSlot, setFilterSlot] = useState<string>('all');
  const [filterClass, setFilterClass] = useState<string>('all');
  const [filterMinLevel, setFilterMinLevel] = useState<string>('');
  const [filterMaxLevel, setFilterMaxLevel] = useState<string>('');
  const [showOnlyCraftable, setShowOnlyCraftable] = useState<boolean>(false);
  const [recipeStats, setRecipeStats] = useState<{totalRecipes: number, discoveredRecipes: number, discoveryPercentage: number} | null>(null);
  const [materialSources, setMaterialSources] = useState<Record<number, MaterialSource[]>>({});
  const [characters, setCharacters] = useState<CharacterOption[]>([]);

  const loadInventory = useCallback(async () => {
    try {
      logger.debug('Loading inventory...');
      const response = await makeAuthenticatedRequest('/crafting/inventory');
      logger.debug('Inventory response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        logger.debug('Inventory data:', data);
        setInventory(data);
      } else {
        const errorData = await response.json();
        logger.error('Inventory error:', errorData);
        throw new Error(errorData.error || 'Failed to load inventory');
      }
    } catch (err) {
      logger.error('Inventory catch error:', err);
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError(`Inventory Error: ${apiError.message}`);
    }
  }, [handleAuthError]);

  const loadRecipes = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/recipes');
      if (response.ok) {
        setRecipes(await response.json());
      } else {
        throw new Error('Failed to load recipes');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError(apiError.message);
    }
  }, [handleAuthError]);

  const loadCraftedItems = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/crafted-items');
      if (response.ok) {
        setCraftedItems(await response.json());
      } else {
        throw new Error('Failed to load crafted items');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError(apiError.message);
    }
  }, [handleAuthError]);

  const loadRecipeStats = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/recipe-stats');
      if (response.ok) {
        setRecipeStats(await response.json());
      } else {
        throw new Error('Failed to load recipe stats');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      logger.error('Failed to load recipe stats:', err);
    }
  }, [handleAuthError]);

  const loadMaterialSources = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/material-sources');
      if (response.ok) {
        setMaterialSources(await response.json());
      } else {
        throw new Error('Failed to load material sources');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      logger.error('Failed to load material sources:', err);
    }
  }, [handleAuthError]);

  const loadCharacters = useCallback(async () => {
    try {
      const response = await makeAuthenticatedRequest('/characters');
      if (response.ok) {
        setCharacters(await response.json());
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      logger.error('Failed to load characters:', err);
    }
  }, [handleAuthError]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      logger.debug('Loading crafting data...');
      await Promise.all([
        loadInventory(),
        loadRecipes(),
        loadCraftedItems(),
        loadRecipeStats(),
        loadMaterialSources(),
        loadCharacters()
      ]);
      logger.debug('Crafting data loaded successfully');
    } catch (err) {
      logger.error('Error loading crafting data:', err);
      setError('Failed to load crafting data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (recipes.length > 0 && !loading) {
      checkAndStartTutorial('crafting-basics');
    }
  }, [recipes, loading, checkAndStartTutorial]);

  const dismantleItem = async (itemId: number) => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/dismantle', {
        method: 'POST',
        body: JSON.stringify({ itemId })
      });
      const data = await response.json();
      if (response.ok) {
        await loadData();
        const parts = data.materialsReturned.map((m: {materialName: string; quantity: number}) =>
          `${m.quantity}x ${m.materialName}`).join(', ');
        addNotification({ type: 'info', title: 'Item Dismantled', message: `${data.message} Recovered: ${parts}` });
      } else {
        addNotification({ type: 'info', title: 'Error', message: data.error || 'Failed to dismantle item' });
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      addNotification({ type: 'info', title: 'Error', message: apiError.message });
    }
  };

  const sellItem = async (itemId: number, characterId: number) => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/sell-item', {
        method: 'POST',
        body: JSON.stringify({ itemId, characterId })
      });
      const data = await response.json();
      if (response.ok) {
        await loadData();
        addNotification({ type: 'info', title: 'Item Sold', message: `+${data.goldEarned} gold` });
      } else {
        addNotification({ type: 'info', title: 'Error', message: data.error || 'Failed to sell item' });
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      addNotification({ type: 'info', title: 'Error', message: apiError.message });
    }
  };

  const sellMaterial = async (materialId: number, quantity: number, characterId: number) => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/sell-material', {
        method: 'POST',
        body: JSON.stringify({ materialId, quantity, characterId })
      });
      const data = await response.json();
      if (response.ok) {
        await loadData();
        addNotification({ type: 'info', title: 'Materials Sold', message: `+${data.goldEarned} gold` });
      } else {
        addNotification({ type: 'info', title: 'Error', message: data.error || 'Failed to sell material' });
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      addNotification({ type: 'info', title: 'Error', message: apiError.message });
    }
  };

  const exchangeMaterial = async (materialId: number, sets: number, characterId: number) => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/exchange-materials', {
        method: 'POST',
        body: JSON.stringify({ materialId, sets, characterId })
      });
      const data = await response.json();
      if (response.ok) {
        await loadData();
        addNotification({ type: 'info', title: 'Materials Exchanged', message: data.message });
      } else {
        addNotification({ type: 'info', title: 'Error', message: data.error || 'Failed to exchange materials' });
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      addNotification({ type: 'info', title: 'Error', message: apiError.message });
    }
  };

  const combineItems = async (itemId1: number, itemId2: number) => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/combine', {
        method: 'POST',
        body: JSON.stringify({ itemId1, itemId2 })
      });
      const data = await response.json();
      if (response.ok) {
        await loadData();
        addNotification({ type: 'levelup', title: 'Items Combined!', message: data.message });
      } else {
        addNotification({ type: 'info', title: 'Error', message: data.error || 'Failed to combine items' });
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      addNotification({ type: 'info', title: 'Error', message: apiError.message });
    }
  };

  const donateItem = async (itemId: number, townId: number) => {
    try {
      const response = await makeAuthenticatedRequest('/crafting/donate', {
        method: 'POST',
        body: JSON.stringify({ itemId, townId })
      });
      const data = await response.json();
      if (response.ok) {
        await loadData();
        addNotification({ type: 'info', title: 'Donation Accepted', message: `${data.message} +${data.standingGained} standing (now ${data.newStanding}/1000)` });
      } else {
        addNotification({ type: 'info', title: 'Error', message: data.error || 'Failed to donate item' });
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      addNotification({ type: 'info', title: 'Error', message: apiError.message });
    }
  };

  const craftItem = async (recipeId: number, characterId: number | null) => {
    if (crafting) return;
    setCrafting(true);
    setError(null);
    try {
      const response = await makeAuthenticatedRequest('/crafting/craft', {
        method: 'POST',
        body: JSON.stringify({ recipeId, characterId })
      });

      if (response.ok) {
        const result = await response.json();
        await loadData();
        setError(null);

        const quality = result.craftedItem.qualityPercent || 100;
        let notificationType: 'info' | 'levelup' | 'achievement' = 'info';
        let title = 'Item Crafted!';
        if (quality >= 95) { notificationType = 'achievement'; title = 'Perfect Craft!'; }
        else if (quality >= 85) { notificationType = 'levelup'; title = 'Excellent Craft!'; }

        let bonusMessage = `You crafted ${result.craftedItem.itemName}! Quality: ${quality}%`;
        if (result.goldFeePaid > 0) bonusMessage += ` (paid ${result.goldFeePaid}g smith's fee)`;
        if (result.bonuses?.hasBonuses) {
          const bonusParts = [];
          if (result.bonuses.qualityBonus) bonusParts.push(`Quality Bonus: ${result.bonuses.qualityBonus}`);
          if (result.bonuses.materialsSaved > 0) bonusParts.push(`Saved ${result.bonuses.materialsSaved} materials!`);
          if (bonusParts.length > 0) bonusMessage += ` 🏰 Town Bonuses: ${bonusParts.join(', ')}`;
        }

        addNotification({ type: notificationType, title, message: bonusMessage });

        if (result.achievementsUnlocked?.length > 0) {
          result.achievementsUnlocked.forEach((achievement: AchievementUnlock) => {
            addNotification({
              type: 'achievement',
              title: `${achievement.icon} Achievement Unlocked!`,
              message: `${achievement.name} - ${achievement.description} (${achievement.currentProgress}/${achievement.requiredValue})`
            });
          });
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to craft item');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError(apiError.message);
    } finally {
      setCrafting(false);
    }
  };

  if (loading) {
    return (
      <div className="crafting-container">
        <div className="crafting-loading">Loading crafting system...</div>
      </div>
    );
  }

  return (
    <div className="crafting-container">
      <div className="crafting-header">
        <div>
          <h2>Crafting Workshop</h2>
          <p>Transform raw materials into powerful equipment and useful items</p>
        </div>
        {recipeStats && (
          <div className="recipe-discovery-stats">
            <div className="discovery-label">Recipes Discovered</div>
            <div className="discovery-count">{recipeStats.discoveredRecipes} / {recipeStats.totalRecipes}</div>
            <div className="discovery-percentage">{recipeStats.discoveryPercentage}%</div>
          </div>
        )}
      </div>

      <div className="crafting-tabs">
        <button
          className={`crafting-tab-btn ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          Inventory ({inventory.length})
        </button>
        <button
          className={`crafting-tab-btn ${activeTab === 'recipes' ? 'active' : ''}`}
          onClick={() => setActiveTab('recipes')}
        >
          Recipes ({recipes.length})
        </button>
        <button
          className={`crafting-tab-btn ${activeTab === 'crafted-items' ? 'active' : ''}`}
          onClick={() => setActiveTab('crafted-items')}
        >
          Crafted Items ({craftedItems.length})
        </button>
      </div>

      {error && (
        <div className="crafting-error">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="crafting-content">
        {activeTab === 'inventory' && (
          <CraftingInventoryTab
            inventory={inventory}
            characters={characters}
            onSellMaterial={sellMaterial}
            onExchangeMaterial={exchangeMaterial}
          />
        )}
        {activeTab === 'recipes' && (
          <CraftingRecipesTab
            recipes={recipes}
            inventory={inventory}
            craftedItems={craftedItems}
            materialSources={materialSources}
            characters={characters}
            filterType={filterType}
            filterSlot={filterSlot}
            filterClass={filterClass}
            filterMinLevel={filterMinLevel}
            filterMaxLevel={filterMaxLevel}
            showOnlyCraftable={showOnlyCraftable}
            crafting={crafting}
            setFilterType={setFilterType}
            setFilterSlot={setFilterSlot}
            setFilterClass={setFilterClass}
            setFilterMinLevel={setFilterMinLevel}
            setFilterMaxLevel={setFilterMaxLevel}
            setShowOnlyCraftable={setShowOnlyCraftable}
            onCraftItem={craftItem}
          />
        )}
        {activeTab === 'crafted-items' && (
          <CraftingCraftedTab
            craftedItems={craftedItems}
            characters={characters}
            onDismantle={dismantleItem}
            onCombine={combineItems}
            onDonate={donateItem}
            onSell={sellItem}
          />
        )}
      </div>
    </div>
  );
};

export default Crafting;
